import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Session, SessionStatus, SessionType, GpsCheckIn } from '../models/Session';
import { appointmentService } from './AppointmentService';

const DEFAULT_DEADLINE_HOURS = 24;

export interface StartSessionInput {
  companyId: string;
  appointmentId?: string | null;
  clientId: string;
  clientName: string;
  rbtId: string;
  rbtName: string;
  supervisorId: string;
  supervisorName: string;
  scheduledStart: Date;
  scheduledEnd: Date;
  clientAddress: string;
  sessionType: SessionType;
  gpsCheckIn?: GpsCheckIn | null;
  verificationPhotoUrl?: string;
}

/**
 * SessionService — reads/writes RBT session documents stored under
 * companies/{companyId}/sessions/{sessionId}, and drives the session
 * lifecycle (start → pending-notes → submitted → approved/needs-revision
 * → revised).
 *
 * Queries filter only by rbtId (no orderBy) so no composite index is
 * required; sorting/section-splitting happens client-side in the page.
 */
export class SessionService {
  private static instance: SessionService;

  private constructor() {}

  static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService();
    }
    return SessionService.instance;
  }

  private sessionsCollection(companyId: string) {
    return collection(db, 'companies', companyId, 'sessions');
  }

  /* --------------------------- lifecycle --------------------------- */

  // Find the canonical session for an appointment, or create it (in-progress).
  // Idempotent by appointmentId, so both Start-Session entry points (Dashboard
  // and My Sessions) and the active-session screen resolve to the SAME record
  // — no duplicates. Used by the Phase 2 mirror. (SESSION_UNIFICATION_PLAN.md.)
  async findOrCreateByAppointment(input: StartSessionInput): Promise<string> {
    if (input.appointmentId) {
      const q = query(
        this.sessionsCollection(input.companyId),
        where('appointmentId', '==', input.appointmentId)
      );
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].id;
    }
    return this.startSession(input);
  }

  // 1. RBT starts a session from an upcoming appointment.
  async startSession(input: StartSessionInput): Promise<string> {
    const ref = doc(this.sessionsCollection(input.companyId));
    const now = Timestamp.now();

    const data: Record<string, any> = {
      id: ref.id,
      companyId: input.companyId,
      appointmentId: input.appointmentId ?? null,
      clientId: input.clientId,
      clientName: input.clientName,
      rbtId: input.rbtId,
      rbtName: input.rbtName,
      supervisorId: input.supervisorId,
      supervisorName: input.supervisorName,
      scheduledStart: Timestamp.fromDate(input.scheduledStart),
      scheduledEnd: Timestamp.fromDate(input.scheduledEnd),
      actualStart: now,
      actualEnd: null,
      status: 'in-progress' as SessionStatus,
      sessionType: input.sessionType,
      clientAddress: input.clientAddress,
      // Empty documentation fields
      dictationTranscript: '',
      dictationAudioUrl: '',
      noteData: null,
      noteTemplate: '',
      submittedAt: null,
      deadline: null,
      supervisorComments: '',
      // Verification (from the existing start flow if available)
      gpsCheckIn: input.gpsCheckIn
        ? {
            lat: input.gpsCheckIn.lat,
            lng: input.gpsCheckIn.lng,
            timestamp: input.gpsCheckIn.timestamp
              ? Timestamp.fromDate(input.gpsCheckIn.timestamp)
              : null,
            verified: !!input.gpsCheckIn.verified,
          }
        : null,
      verificationPhotoUrl: input.verificationPhotoUrl ?? '',
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(ref, data);
    // Symmetric with completeLinkedAppointment: mark the linked appointment
    // 'in-progress' so supervisor/calendar views reflect that the session has
    // started (it otherwise stays 'scheduled' until end). Best-effort.
    if (input.appointmentId) {
      await this.startLinkedAppointment(input.appointmentId);
    }
    return ref.id;
  }

  // Best-effort: mark an appointment 'in-progress' when its session starts.
  // Never throws into the caller — appointment bookkeeping shouldn't break start.
  private async startLinkedAppointment(appointmentId: string): Promise<void> {
    try {
      await appointmentService.updateAppointmentStatus(appointmentId, 'in-progress');
    } catch (e) {
      console.error('startLinkedAppointment failed:', e);
    }
  }

  // 2. RBT ends an in-progress session → pending-notes with a deadline.
  async endSession(
    companyId: string,
    sessionId: string,
    deadlineHours: number = DEFAULT_DEADLINE_HOURS
  ): Promise<void> {
    const now = new Date();
    const deadline = new Date(now.getTime() + deadlineHours * 60 * 60 * 1000);
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), {
      actualEnd: Timestamp.fromDate(now),
      status: 'pending-notes',
      deadline: Timestamp.fromDate(deadline),
      updatedAt: Timestamp.fromDate(now),
    });
    // The session has happened — close its appointment so it drops off the
    // RBT's Upcoming list and the calendar. (Phase 5; see
    // SESSION_UNIFICATION_PLAN.md.)
    await this.completeLinkedAppointment(companyId, sessionId);
  }

  // Best-effort: mark the session's linked appointment 'completed'. Keyed to
  // the canonical companies/sessions record, so it is reused unchanged once the
  // active-session screen is repointed here (Phase 2). Never throws into the
  // caller — appointment bookkeeping shouldn't break the session flow.
  private async completeLinkedAppointment(companyId: string, sessionId: string): Promise<void> {
    try {
      const snap = await getDoc(doc(this.sessionsCollection(companyId), sessionId));
      const appointmentId = snap.data()?.appointmentId as string | null | undefined;
      if (appointmentId) {
        await appointmentService.updateAppointmentStatus(appointmentId, 'completed');
      }
    } catch (e) {
      console.error('completeLinkedAppointment failed:', e);
    }
  }

  // Persist dictation transcript (and optional audio URL) while still
  // in pending-notes — does not change status.
  async saveDictation(
    companyId: string,
    sessionId: string,
    dictationTranscript: string,
    dictationAudioUrl?: string
  ): Promise<void> {
    const update: Record<string, any> = {
      dictationTranscript,
      updatedAt: Timestamp.now(),
    };
    if (dictationAudioUrl !== undefined) update.dictationAudioUrl = dictationAudioUrl;
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), update);
  }

  // Privacy Mode: log the start of a privacy pause (append an open window).
  async startPrivacyPause(companyId: string, sessionId: string): Promise<void> {
    const ref = doc(this.sessionsCollection(companyId), sessionId);
    const snap = await getDoc(ref);
    const pauses: any[] = (snap.data()?.privacyPauses as any[]) || [];
    pauses.push({ startTime: Timestamp.now(), endTime: null });
    await updateDoc(ref, { privacyPauses: pauses, updatedAt: Timestamp.now() });
  }

  // Privacy Mode: close the most recent open privacy pause with an endTime.
  async endPrivacyPause(companyId: string, sessionId: string): Promise<void> {
    const ref = doc(this.sessionsCollection(companyId), sessionId);
    const snap = await getDoc(ref);
    const pauses: any[] = (snap.data()?.privacyPauses as any[]) || [];
    for (let i = pauses.length - 1; i >= 0; i--) {
      if (!pauses[i].endTime) {
        pauses[i].endTime = Timestamp.now();
        break;
      }
    }
    await updateDoc(ref, { privacyPauses: pauses, updatedAt: Timestamp.now() });
  }

  // 3. RBT submits notes → submitted (awaiting BCBA review).
  async submitNotes(
    companyId: string,
    sessionId: string,
    noteData: Record<string, any>,
    noteTemplate: string
  ): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), {
      status: 'submitted',
      submittedAt: now,
      noteData,
      noteTemplate,
      updatedAt: now,
    });
  }

  // 4. BCBA approves & signs.
  async approveSession(
    companyId: string,
    sessionId: string,
    supervisorSignature: string,
    parentSummary?: string
  ): Promise<void> {
    const now = Timestamp.now();
    const update: Record<string, any> = {
      status: 'approved',
      supervisorSignature,
      approvedAt: now,
      updatedAt: now,
    };
    if (parentSummary !== undefined) update.parentSummary = parentSummary;
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), update);
    // Reaffirm the appointment is closed (idempotent; covers approvals of
    // sessions that somehow weren't closed at end).
    await this.completeLinkedAppointment(companyId, sessionId);
  }

  // 5. BCBA requests a revision with feedback.
  async requestRevision(
    companyId: string,
    sessionId: string,
    supervisorComments: string
  ): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), {
      status: 'needs-revision',
      supervisorComments,
      revisionRequestedAt: now,
      updatedAt: now,
    });
  }

  // Persist the generated clinical-note PDF download URL.
  async saveNotePdfUrl(companyId: string, sessionId: string, notePdfUrl: string): Promise<void> {
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), {
      notePdfUrl,
      updatedAt: Timestamp.now(),
    });
  }

  // Persist the (possibly BCBA-edited) parent summary and mark it approved.
  async saveParentSummary(
    companyId: string,
    sessionId: string,
    parentSummary: string,
    editedByBCBA: boolean = false
  ): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), {
      parentSummary,
      parentSummaryApprovedAt: now,
      parentSummaryEditedByBCBA: editedByBCBA,
      updatedAt: now,
    });
  }

  // 6. RBT resubmits after a revision.
  async resubmitNotes(
    companyId: string,
    sessionId: string,
    noteData: Record<string, any>
  ): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(this.sessionsCollection(companyId), sessionId), {
      status: 'revised',
      noteData,
      submittedAt: now,
      updatedAt: now,
    });
  }

  /* ----------------------------- reads ----------------------------- */

  async getSessionsByRbt(companyId: string, rbtId: string): Promise<Session[]> {
    const q = query(this.sessionsCollection(companyId), where('rbtId', '==', rbtId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => this.mapFirestoreToSession(companyId, d.id, d.data()));
  }

  // Sessions awaiting BCBA review — same statuses the BCBA Review screen treats
  // as "pending" (submitted/revised). Used for the supervisor dashboard count
  // so it agrees with the review page instead of the legacy sessionNotes
  // collection (fixes the "1 pending but empty page" mismatch). (Phase 4.)
  async getPendingReviewSessions(companyId: string): Promise<Session[]> {
    const q = query(
      this.sessionsCollection(companyId),
      where('status', 'in', ['submitted', 'revised'])
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => this.mapFirestoreToSession(companyId, d.id, d.data()));
  }

  // Real-time subscription — invokes cb() whenever any of the RBT's
  // session documents change. Returns an unsubscribe function.
  subscribeSessionsByRbt(
    companyId: string,
    rbtId: string,
    cb: (sessions: Session[]) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const q = query(this.sessionsCollection(companyId), where('rbtId', '==', rbtId));
    return onSnapshot(
      q,
      (snapshot) => {
        cb(snapshot.docs.map((d) => this.mapFirestoreToSession(companyId, d.id, d.data())));
      },
      (err) => {
        console.error('SessionService subscription error:', err);
        if (onError) onError(err);
      }
    );
  }

  // Real-time subscription to ALL session documents in the company
  // (for the BCBA review screen). Returns an unsubscribe function.
  subscribeCompanySessions(
    companyId: string,
    cb: (sessions: Session[]) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      this.sessionsCollection(companyId),
      (snapshot) => {
        cb(snapshot.docs.map((d) => this.mapFirestoreToSession(companyId, d.id, d.data())));
      },
      (err) => {
        console.error('SessionService company subscription error:', err);
        if (onError) onError(err);
      }
    );
  }

  async getSession(companyId: string, sessionId: string): Promise<Session | null> {
    const ref = doc(this.sessionsCollection(companyId), sessionId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return this.mapFirestoreToSession(companyId, snap.id, snap.data());
  }

  async updateSessionStatus(
    companyId: string,
    sessionId: string,
    status: SessionStatus
  ): Promise<void> {
    const ref = doc(this.sessionsCollection(companyId), sessionId);
    await updateDoc(ref, { status, updatedAt: Timestamp.fromDate(new Date()) });
  }

  /* --------------------------- mapping ----------------------------- */

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    return null;
  }

  private mapFirestoreToSession(companyId: string, id: string, data: any): Session {
    const gps = data.gpsCheckIn
      ? {
          lat: data.gpsCheckIn.lat,
          lng: data.gpsCheckIn.lng,
          timestamp: this.toDate(data.gpsCheckIn.timestamp),
          verified: !!data.gpsCheckIn.verified,
        }
      : null;

    return {
      id,
      companyId: data.companyId || companyId,
      appointmentId: data.appointmentId ?? null,
      clientId: data.clientId || '',
      clientName: data.clientName || 'Unknown Client',
      rbtId: data.rbtId || '',
      rbtName: data.rbtName || '',
      supervisorId: data.supervisorId || '',
      supervisorName: data.supervisorName || '',
      scheduledStart: this.toDate(data.scheduledStart) || new Date(),
      scheduledEnd: this.toDate(data.scheduledEnd) || new Date(),
      actualStart: this.toDate(data.actualStart),
      actualEnd: this.toDate(data.actualEnd),
      status: (data.status as SessionStatus) || 'scheduled',
      sessionType: data.sessionType || 'direct-therapy',
      clientAddress: data.clientAddress || '',
      dictationTranscript: data.dictationTranscript || '',
      dictationAudioUrl: data.dictationAudioUrl || '',
      noteData: data.noteData ?? null,
      noteTemplate: data.noteTemplate || '',
      submittedAt: this.toDate(data.submittedAt),
      deadline: this.toDate(data.deadline),
      supervisorComments: data.supervisorComments || '',
      supervisorSignature: data.supervisorSignature || '',
      approvedAt: this.toDate(data.approvedAt),
      revisionRequestedAt: this.toDate(data.revisionRequestedAt),
      parentSummary: data.parentSummary || '',
      parentSummaryApprovedAt: this.toDate(data.parentSummaryApprovedAt),
      parentSummaryEditedByBCBA: !!data.parentSummaryEditedByBCBA,
      notePdfUrl: data.notePdfUrl || '',
      gpsCheckIn: gps,
      verificationPhotoUrl: data.verificationPhotoUrl || '',
      createdAt: this.toDate(data.createdAt) || new Date(),
      updatedAt: this.toDate(data.updatedAt) || new Date(),
    };
  }
}

export const sessionService = SessionService.getInstance();
